import type { GridRowId, GridRowModesModel, MuiEvent } from '@mui/x-data-grid-premium';
import { GridRowModes } from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';
import { isPalletTagDrawerOpen, useSideDrawerState } from 'src/components/containers/SideDrawer';
import type { RequestQueue } from 'src/hooks/useQueuedRequests';
import type { GridRow, GridValidationModel } from 'src/lib/gridRows.store';
import { isDraftAwaitingCreate, isEmptyDiff } from 'src/lib/gridRows.store';
import type { GridApiRef } from '../DataGrid';
import { useEditableGridRows } from './useEditableGridRows';

export type GridRowSaveChangesReason = 'saveRow' | 'saveCell';

/** Marked by `EnterNewRowButton` (`EditableDataGrid.tsx`) on the add affordance itself. */
const ADD_ROW_AFFORDANCE_SELECTOR = '[data-grid-add-row]';

const isAddRowAffordance = (target: EventTarget | null | undefined): boolean =>
  target instanceof Element && target.closest(ADD_ROW_AFFORDANCE_SELECTOR) !== null;

const fieldValue = (row: object | undefined, field: string): unknown => (row as Record<string, unknown>)?.[field];

export function useGridSavePipeline<R extends object, I>(
  apiRef: GridApiRef,
  {
    afterSaveChanges,
    getRowId,
    handleRequest,
    onCreate,
    onEdit,
    onMutationResolved,
    rowToInput,
    saveChanges: _saveChanges,
    setRowModesModel,
    validationModel,
  }: {
    afterSaveChanges?: (row: GridRow<R>) => void;
    getRowId: (row: GridRow<R>) => GridRowId;
    handleRequest: RequestQueue<R, I, 'create' | 'update'>;
    onCreate: (payload: { input: I }) => Promise<R>;
    onEdit: (payload: { id: number; input: I }) => Promise<R>;
    /** Runs after successful state reconciliation and must not throw. */
    onMutationResolved?: (previousRow: GridRow<R>, acceptedRow: Partial<R>, resolvedRow: GridRow<R>) => void;
    rowToInput: (changes: Partial<GridRow<R>>, row: GridRow<R>) => I;
    saveChanges?: (row: GridRow<R>, reason: GridRowSaveChangesReason) => GridRow<R> | undefined;
    setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
    /** Static or row-dependent; forwarded to `edit`, which resolves it per row. */
    validationModel: GridValidationModel<Record<string, unknown>>;
  }
) {
  const edit = useEditableGridRows((store) => store.edit);
  const getLocalRow = useEditableGridRows((store) => store.getLocalRow);
  const recordInteraction = useEditableGridRows((store) => store.recordInteraction);

  const saveChanges = useCallback(
    (row: GridRow<R>, reason: GridRowSaveChangesReason) => {
      const handledResult = _saveChanges?.(row, reason);

      if (handledResult) {
        afterSaveChanges?.(row);
        return handledResult;
      }

      // Branch on the authoritative store state, NOT the MUI grid's lagging `row.status`,
      // so a double-fired draft commit can't enqueue two creates (ghost twin — TAS-3271).
      const previousRow = getLocalRow(row.rowId);
      const isCreate = isDraftAwaitingCreate(previousRow);

      const stateMutation = edit(row, { validationModel });

      // no changes
      if (!stateMutation) {
        return row;
      }

      /**
       * EMPTY-DIFF GUARD — structural, every grid, both arms.
       *
       *   diff ─▶ empty ──╳──▶ handleRequest        no request leaves the browser
       *        └─ real  ──────▶ handleRequest ─▶ wire
       *
       * `edit()` already refuses an empty diff above its own side effects, so the one
       * path that reaches here is the fatal-validation strip: `edit()` deletes every
       * fatal issue path out of the diff and can empty it that way. Such a row is
       * ALREADY carrying its validation error — it must not be settled as accepted, so
       * this refuses the send and leaves the row's error state alone.
       */
      if (isEmptyDiff(stateMutation.payload)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Grid save refused: the diff carries no field, so no mutation was sent.', {
            payload: stateMutation.payload,
            reason,
            rowId: row.rowId,
          });
        }

        return row;
      }

      const resolve = onMutationResolved
        ? (accepted: Partial<R>) => {
            stateMutation.resolve(accepted as R);
            onMutationResolved(previousRow, accepted, getLocalRow(row.rowId));
          }
        : stateMutation.resolve;

      if (isCreate) {
        handleRequest(row.rowId, {
          event: onCreate,
          eventType: 'create',
          unpackPayload: () => ({ input: rowToInput(stateMutation.payload, row) }),
          resolve,
          reject: stateMutation.reject,
        });
      } else {
        handleRequest(row.rowId, {
          event: onEdit,
          eventType: 'update',
          coalescable: true,
          unpackPayload: () => ({ id: stateMutation.getLatestId(), input: rowToInput(stateMutation.payload, row) }),
          resolve,
          reject: stateMutation.reject,
        });
      }

      afterSaveChanges?.(row);

      return row;
    },
    [
      validationModel,
      _saveChanges,
      afterSaveChanges,
      edit,
      getLocalRow,
      handleRequest,
      onCreate,
      onEdit,
      onMutationResolved,
      rowToInput,
    ]
  );

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;

    /**
     * Per-cell saves during row edit are a product contract: they drive validation timing,
     * incremental persistence, and the TAS-3271 two-source dedup — not replaceable by
     * `onRowEditStop` (see PR #4506 for the measured MUI evidence).
     */
    return api.subscribeEvent('cellFocusOut', (params, e) => {
      recordInteraction(params.row.rowId, { visited: params.field });

      setTimeout(() => {
        const currentApi = apiRef.current;
        if (!currentApi) return;

        const rowMode = currentApi.getRowMode(params.row.rowId);

        if ((e as MuiEvent<MouseEvent>)?.type === 'mouseup' || rowMode === 'view') {
          return;
        }

        const updatedRow = currentApi.getRowWithUpdatedValues(params.row.rowId, params.field) as GridRow<R>;
        const storedRow = getLocalRow(params.row.rowId);

        /**
         * ADD-AFFORDANCE BLUR — the button owns what happens next, not this save.
         *
         *   blur ─▶ add button · draft · cell value unchanged ──╳──▶ saveCell
         *   every other blur ─────────────────────────────────────▶ saveCell
         *
         * A `valueSetter` that defaults a sibling field makes an untouched draft
         * diff non-empty, so without the cell-value check the press persists a
         * defaults-only row. Typing first changes the cell, so that row still saves.
         */
        if (
          isAddRowAffordance((e as MuiEvent<FocusEvent>)?.relatedTarget) &&
          isDraftAwaitingCreate(storedRow) &&
          isEqual(fieldValue(updatedRow, params.field), fieldValue(storedRow, params.field))
        ) {
          return;
        }

        saveChanges(updatedRow, 'saveCell');
      });
    });
  }, [saveChanges, apiRef, recordInteraction, getLocalRow]);

  const openDrawerId = useSideDrawerState((state) => state.openId);

  const processRowUpdate = useCallback(
    (row: GridRow<R>): GridRow<R> => {
      // Hold commit while the pallet-tag drawer owns this row (avoids clobbering its writes).
      if (isPalletTagDrawerOpen(openDrawerId)) {
        setTimeout(() => {
          if (!apiRef.current) return;
          setRowModesModel((prev) => ({ ...prev, [getRowId(row)]: { mode: GridRowModes.Edit } }));
        });
        return row;
      }

      recordInteraction(row.rowId, { saved: true });

      return saveChanges(row, 'saveRow');
    },
    [saveChanges, openDrawerId, recordInteraction, setRowModesModel, getRowId]
  );

  return { processRowUpdate, saveChanges };
}
